import React from 'react';
import SEOHead from './SEOHead';
import companyConfig from '../config/companyConfig';

function About() {
  const values = [
    {
      icon: 'fa-trophy',
      title: 'Excellence',
      description: 'We deliver exceptional quality in every project, exceeding expectations through meticulous attention to detail.'
    },
    {
      icon: 'fa-shield-alt',
      title: 'Integrity',
      description: 'Trust and transparency guide our relationships with clients, partners, and team members.'
    },
    {
      icon: 'fa-lightbulb',
      title: 'Innovation',
      description: 'We embrace cutting-edge technologies and creative solutions to solve complex business challenges.'
    },
    {
      icon: 'fa-users',
      title: 'Collaboration',
      description: 'Success comes from working together, combining diverse perspectives and expertise.'
    }
  ];

  const leadership = [
    {
      name: 'Deepa Stanley',
      title: 'President',
      expertise: 'Visionary leader with 20+ years in enterprise IT'
    },
    {
      name: 'Varghese Mathew',
      title: 'Chief Technology Officer',
      expertise: 'SAP developer and architect with 27+ yrs of experience'
    }
  ];

  return (
    <>
      <SEOHead 
        title="About Time2Find - Expert IT Solutions & Digital Transformation Team"
        description="Learn about Time2Find's mission, values, and leadership team. 25+ years of expertise in SAP Implementation, Cloud Solutions, Cybersecurity, Data Analytics, and enterprise IT consulting."
        keywords="About Time2Find, IT consulting team, SAP experts, digital transformation specialists, enterprise solutions, company leadership, business values, technology expertise"
        canonical="https://time2find.com/about"
        schemaType="AboutPage"
      />
      
      <div className="about-page">
        {/* Hero Section */}
        <section className="about-hero">
        <div className="about-hero-container">
          <h1 className="about-main-heading">
            Bringing Your Vision to Life with <span className="highlight-blue">Expertise</span> and <span className="highlight-blue">Dedication</span>
          </h1>
          
          <div className="about-hero-image-wrapper">
            <img 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=500&fit=crop" 
              alt="Time2Find team collaborating in modern office"
              className="about-hero-image"
            />
          </div>

          {/* Stat Pills */}
          <div className="about-stats-pills">
            <div className="stat-pill">
              <span className="stat-pill-number">25+</span>
              <span className="stat-pill-label">Years of Experience</span>
            </div>
            <div className="stat-pill">
              <span className="stat-pill-number">50+</span>
              <span className="stat-pill-label">Global Clients</span>
            </div>
            <div className="stat-pill">
              <span className="stat-pill-number">50+</span>
              <span className="stat-pill-label">Projects Completed</span>
            </div>
            <div className="stat-pill">
              <span className="stat-pill-number">25</span>
              <span className="stat-pill-label">Countries Served</span>
            </div>
          </div>

          {/* About Paragraph */}
          <p className="about-intro-text">
            For over 25 years, we've led enterprise IT transformation by helping global organizations navigate complex digital change and achieve sustainable growth through technology excellence. Our commitment to innovation and client success has made us a trusted partner for Fortune 500 companies worldwide.
          </p>
        </div>
      </section>

      {/* Our Story Panel */}
      <section className="about-story-section">
        <div className="about-story-container">
          <div className="about-story-content">
            <div className="about-story-text">
              <h2 className="about-story-heading">Our Story</h2>
              <p className="about-story-paragraph">
                Founded in 2000, Time2Find began with a simple mission to bridge the gap between enterprise ambitions and technological reality. Starting as a small team of passionate technologists, we've grown into a global powerhouse serving clients across six continents.
              </p>
              <p className="about-story-paragraph">
                Our journey has been marked by continuous innovation, unwavering commitment to excellence, and a deep understanding that technology is only as powerful as the people who wield it. Today, we're proud to be recognized as industry leaders in digital transformation.
              </p>
              <div className="about-story-stats">
                <div className="story-stat">
                  <div className="story-stat-icon"><i className="fas fa-award"></i></div>
                  <div>
                    <div className="story-stat-number">98%</div>
                    <div className="story-stat-label">Client Satisfaction</div>
                  </div>
                </div>
                <div className="story-stat">
                  <div className="story-stat-icon"><i className="fas fa-star"></i></div>
                  <div>
                    <div className="story-stat-number">15+</div>
                    <div className="story-stat-label">Industry Awards</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="about-story-image">
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=700&fit=crop" 
                alt="Executive team meeting discussing strategy"
                className="story-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CEO/Founder Section */}
      <section className="ceo-founder-section">
        <div className="ceo-founder-container">
          <div className="ceo-founder-content">
            <div className="ceo-founder-image-wrapper">
              <img 
                src={companyConfig.ceoFounder.image} 
                alt={`${companyConfig.ceoFounder.name}, ${companyConfig.ceoFounder.title}`}
                className="ceo-founder-image"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x500/0D6EFD/FFFFFF?text=CEO+Photo';
                }}
              />
            </div>
            
            <div className="ceo-founder-info">
              <div className="ceo-founder-header">
                <h2 className="ceo-founder-name">{companyConfig.ceoFounder.name}</h2>
                <p className="ceo-founder-title">{companyConfig.ceoFounder.title}</p>
              </div>
              
              <blockquote className="ceo-founder-quote">
                {companyConfig.ceoFounder.quote}
              </blockquote>
              
              <p className="ceo-founder-bio">
                {companyConfig.ceoFounder.bio}
              </p>
              
              <div className="ceo-founder-achievements">
                <h3 className="achievements-title">Key Achievements</h3>
                <ul className="achievements-list">
                  {companyConfig.ceoFounder.achievements.map((achievement, index) => (
                    <li key={index} className="achievement-item">
                      <i className="fas fa-check-circle"></i>
                      <span>{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="about-values-section">
        <div className="about-values-container">
          <h2 className="about-section-heading">Our Values</h2>
          <p className="about-section-subheading">
            The principles that guide everything we do
          </p>
          
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon-circle">
                  <i className={`fas ${value.icon}`}></i>
                </div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="about-leadership-section">
        <div className="about-leadership-container">
          <h2 className="about-section-heading">Leadership Team</h2>
          <p className="about-section-subheading">
            Meet the visionaries driving our mission forward
          </p>
          
          <div className="leadership-grid">
            {leadership.map((leader, index) => (
              <div key={index} className="leader-card">
                <div className="leader-info">
                  <h3 className="leader-name">{leader.name}</h3>
                  <p className="leader-title">{leader.title}</p>
                  <p className="leader-expertise">
                    <i className="fas fa-briefcase"></i> {leader.expertise}
                  </p>
                </div>
                <div className="leader-accent-bar"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Industries Section - Simple & Clean */}
      {/* Development Methods Section - 3D Animated */}
      <section className="dev-methods-section">
        <div className="dev-methods-container">
          <div className="dev-methods-header">
            <div className="dev-methods-badge">
              <i className="fas fa-code-branch"></i>
            </div>
            <h2 className="dev-methods-title">{companyConfig.developmentMethods.title}</h2>
            <p className="dev-methods-subtitle">{companyConfig.developmentMethods.subtitle}</p>
            <p className="dev-methods-description">{companyConfig.developmentMethods.description}</p>
          </div>

          <div className="dev-methods-3d-container">
            {companyConfig.developmentMethods.methods.map((method, index) => (
              <div 
                key={method.id} 
                className="dev-method-card-3d"
                style={{ '--card-delay': `${index * 0.2}s`, '--card-color': method.color }}
              >
                <div className="card-3d-inner">
                  {/* Front of Card */}
                  <div className="card-3d-front">
                    <div className="method-card-header" style={{ background: method.gradient }}>
                      <div className="method-icon-3d">
                        <i className={`fas ${method.icon}`}></i>
                        <div className="icon-glow"></div>
                      </div>
                      <h3 className="method-card-title">{method.title}</h3>
                    </div>
                    
                    <div className="method-card-body">
                      <p className="method-card-description">{method.description}</p>
                      
                      <div className="method-features">
                        {method.features.map((feature, idx) => (
                          <div key={idx} className="method-feature-item">
                            <div className="feature-bullet" style={{ background: method.color }}></div>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="card-3d-reflection"></div>
                  </div>

                  {/* Floating 3D Elements */}
                  <div className="floating-elements">
                    <div className="float-cube" style={{ background: method.gradient }}></div>
                    <div className="float-circle" style={{ borderColor: method.color }}></div>
                    <div className="float-ring" style={{ borderColor: method.color }}></div>
                  </div>
                </div>

                {/* Number Badge */}
                <div className="method-number-badge" style={{ background: method.gradient }}>
                  {String(method.id).padStart(2, '0')}
                </div>
              </div>
            ))}
          </div>

          {/* 3D Background Grid */}
          <div className="dev-methods-bg-grid">
            {[...Array(100)].map((_, index) => (
              <div 
                key={index} 
                className="grid-cell"
                style={{ '--cell-delay': `${Math.random() * 3}s` }}
              ></div>
            ))}
          </div>
        </div>
      </section>
      </div>
    </>
  );
}

export default About;